import { CoreTypes } from '@vp/shared-types';
import type { ClipPayload } from '@vp/core';
import { live } from './bridge';
import { logger, reportStorageError, storage } from '../platform';
import { useApp } from '../stores/app';
import { t } from '../i18n';
import { importSvgText } from './svg-import';
import { profileLocal } from '../profiles/local';
import { currentProfileId } from '../profiles/state';
import { ask } from '../dialogs';
import { formatBytes } from '../i18n';
import { resolveRefs, withRefs, type ClipRefsPayload } from './clip-parts';

/**
 * Copy / paste (§4.6). Internal format travels inside `text/html` as a base64
 * marker, so it works between canvases, tabs and browsers without custom MIME
 * types. A same-origin fallback buffer covers denied clipboard access.
 */
const MARKER = 'vp-clip:';
const MAX_INLINE_ATTACHMENTS = 4 * 1024 * 1024;

interface WirePayload extends ClipRefsPayload {
  attachments?: Record<string, string>;
}

let internal: WirePayload | null = null;

function toBase64(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

function fromBase64(b: string): string {
  const bin = atob(b);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
}

export function encodeHtml(p: WirePayload, text: string): string {
  const b64 = toBase64(JSON.stringify(p));
  return `<meta charset="utf-8"><div>${escapeHtml(text).replace(/\n/g, '<br>')}</div><!--${MARKER}${b64}-->`;
}

export function decodeHtml(html: string): WirePayload | null {
  const i = html.indexOf('<!--' + MARKER);
  if (i < 0) return null;
  const end = html.indexOf('-->', i);
  if (end < 0) return null;
  try {
    const p = JSON.parse(fromBase64(html.slice(i + 4 + MARKER.length, end))) as WirePayload;
    return p.format === 'vp-clip' && Array.isArray(p.objects) ? p : null;
  } catch {
    return null;
  }
}

/** Plain text of text-bearing objects in the payload. */
export function payloadText(p: ClipPayload): string {
  const out: string[] = [];
  for (const o of p.objects) {
    const s = o.props['text'] ?? o.props['title'] ?? o.props['label'];
    if (typeof s === 'string' && s.trim()) out.push(s);
  }
  return out.join('\n\n');
}

async function blobToDataUrl(b: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(b);
  });
}

async function withAttachments(p: ClipPayload): Promise<WirePayload> {
  const out: WirePayload = { ...p, attachments: {} };
  let total = 0;
  for (const o of p.objects) {
    const h = o.props['attachment'];
    if (o.type !== CoreTypes.image || typeof h !== 'string') continue;
    const blob = await storage.getAttachment(h);
    if (!blob || total + blob.size > MAX_INLINE_ATTACHMENTS) continue;
    total += blob.size;
    out.attachments![h] = await blobToDataUrl(blob);
  }
  return out;
}

async function ensureAttachments(p: WirePayload): Promise<void> {
  for (const [hash, url] of Object.entries(p.attachments ?? {})) {
    if (await storage.hasAttachment(hash)) continue;
    const blob = await (await fetch(url)).blob();
    await putAttachmentSafe(blob);
  }
}

/** Store a file; when the storage refuses it (full), say so instead of pasting a picture that would vanish. */
async function putAttachmentSafe(blob: Blob, mime?: string): Promise<string> {
  try {
    return await storage.putAttachment(blob, mime);
  } catch (e) {
    const kind = reportStorageError(e);
    useApp().notify('error', kind === 'quota' ? t('storage.attachmentFailed') : t('error.unknown', { msg: e instanceof Error ? e.message : String(e) }), 8000);
    throw e;
  }
}

function editableTarget(e: Event): boolean {
  const el = (e.target as HTMLElement | null) ?? document.activeElement;
  if (!el || !(el instanceof HTMLElement)) return false;
  return el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName);
}

/** The canvas copied from: large parts travel by reference to it (clip-parts.ts). */
function withSource(p: ClipPayload): ClipRefsPayload {
  const canvas = useApp().active;
  return canvas ? withRefs(p, { profile: currentProfileId(), canvas }) : p;
}

function persistInternal(p: WirePayload): void {
  internal = p;
  try {
    const s = JSON.stringify(p);
    if (s.length < 5_000_000) profileLocal.setItem('vp.clip', s);
  } catch {
    /* quota or disabled storage */
  }
}

function readInternal(): WirePayload | null {
  if (internal) return internal;
  try {
    const s = profileLocal.getItem('vp.clip');
    return s ? (JSON.parse(s) as WirePayload) : null;
  } catch {
    return null;
  }
}

async function copySelection(e: ClipboardEvent | null, cut: boolean): Promise<boolean> {
  const ed = live.editor;
  if (!ed || ed.selection.length === 0) return false;
  const made = cut ? ed.cutPayload() : ed.copyPayload();
  if (!made) return false;
  const payload = withSource(made);
  ed.resetPasteOffset();
  const text = payloadText(payload);
  if (e?.clipboardData) {
    // synchronous part: html with the inline payload (attachments are added to the internal buffer)
    e.clipboardData.setData('text/plain', text);
    e.clipboardData.setData('text/html', encodeHtml(payload, text));
    e.preventDefault();
  }
  const wire = await withAttachments(payload);
  persistInternal(wire);
  logger.debug('clipboard.copy', { count: payload.objects.length, cut });
  return true;
}

export async function pastePayload(p: WirePayload): Promise<void> {
  const ed = live.editor;
  if (!ed) return;
  const fallback = readInternal();
  // the html marker may omit attachments; merge them from the internal buffer when it is the same copy
  if (fallback && fallback.attachments && !p.attachments) p = { ...p, attachments: fallback.attachments };
  try {
    await ensureAttachments(p);
  } catch {
    return; // the storage refused the pictures (already reported)
  }
  const canvas = useApp().active;
  if (canvas) {
    p = await resolveRefs(p, { profile: currentProfileId(), canvas }, { confirmBig: (bytes) => ask(t('clipboard.bigData', { size: formatBytes(bytes) })) });
  }
  ed.paste(p);
}

/** SVG markup (a file or copied text) becomes editable objects; false when it is not a usable SVG. */
export async function pasteSvg(text: string): Promise<boolean> {
  if (!/^\s*(<\?xml[^>]*>\s*)?(<!--[\s\S]*?-->\s*)*(<!DOCTYPE[^>]*>\s*)?<svg[\s>]/i.test(text)) return false;
  const r = await importSvgText(text, (b) => putAttachmentSafe(b, b.type).catch(() => null));
  if (!r) return false;
  const app = useApp();
  if (!r.count) app.notify('warning', t('import.svgEmpty'));
  else if (r.skipped) app.notify('info', t('import.svgPartial', { n: r.count, skipped: r.skipped }), 6000);
  return true;
}

const isSvgFile = (f: File) => f.type === 'image/svg+xml' || /\.svg$/i.test(f.name);

async function pasteImageBlob(blob: Blob): Promise<void> {
  const ed = live.editor;
  if (!ed) return;
  const hash = await putAttachmentSafe(blob, blob.type).catch(() => null);
  if (!hash) return;
  let w = 400;
  let h = 300;
  try {
    const bmp = await createImageBitmap(blob);
    w = bmp.width;
    h = bmp.height;
    bmp.close();
  } catch {
    /* keep defaults */
  }
  await ed.pasteImage(hash, w, h);
}

/** Paste from a DataTransfer (paste event or drop). */
export async function pasteFromData(dt: DataTransfer): Promise<boolean> {
  const html = dt.getData('text/html');
  const p = html ? decodeHtml(html) : null;
  if (p) {
    await pastePayload(p);
    return true;
  }
  for (const f of Array.from(dt.files)) {
    if (isSvgFile(f) && (await pasteSvg(await f.text()))) return true;
    if (f.type.startsWith('image/')) {
      await pasteImageBlob(f);
      return true;
    }
  }
  // HTML without our marker: only plain text is taken (§4.6)
  const text = dt.getData('text/plain');
  if (text && text.trim()) {
    if (await pasteSvg(text)) return true;
    await live.editor?.pasteText(text);
    return true;
  }
  return false;
}

/** Menu-driven paste via the async Clipboard API, with the internal buffer as fallback. */
export async function pasteFromMenu(notify: (level: 'info' | 'warning' | 'error', m: string) => void): Promise<void> {
  try {
    const items = await navigator.clipboard.read();
    for (const it of items) {
      if (it.types.includes('text/html')) {
        const p = decodeHtml(await (await it.getType('text/html')).text());
        if (p) return void (await pastePayload(p));
      }
      if (it.types.includes('image/svg+xml') && (await pasteSvg(await (await it.getType('image/svg+xml')).text()))) return;
      const img = it.types.find((x) => x.startsWith('image/'));
      if (img) return void (await pasteImageBlob(await it.getType(img)));
      if (it.types.includes('text/plain')) {
        const text = await (await it.getType('text/plain')).text();
        if (text.trim()) {
          if (await pasteSvg(text)) return;
          return void (await live.editor?.pasteText(text));
        }
      }
    }
  } catch {
    notify('warning', t('clipboard.denied'));
    const p = readInternal();
    if (p) await pastePayload(p);
  }
}

export async function copyFromMenu(cut: boolean, notify: (level: 'info' | 'warning' | 'error', m: string) => void): Promise<void> {
  const ed = live.editor;
  if (!ed) return;
  const made = ed.copyPayload();
  if (!made) return;
  const payload = withSource(made);
  const text = payloadText(payload);
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([text], { type: 'text/plain' }),
        'text/html': new Blob([encodeHtml(payload, text)], { type: 'text/html' }),
      }),
    ]);
  } catch {
    notify('warning', t('clipboard.denied'));
  }
  persistInternal(await withAttachments(payload));
  if (cut) ed.deleteSelection();
}

export function installClipboard(): () => void {
  const onCopy = (e: ClipboardEvent) => {
    if (editableTarget(e) || !live.editor) return;
    void copySelection(e, false);
  };
  const onCut = (e: ClipboardEvent) => {
    if (editableTarget(e) || !live.editor || live.editor.readOnly) return;
    void copySelection(e, true);
  };
  const onPaste = (e: ClipboardEvent) => {
    if (editableTarget(e) || !live.editor || live.editor.readOnly) return;
    e.preventDefault();
    if (e.clipboardData) {
      void pasteFromData(e.clipboardData).then(async (ok) => {
        if (!ok) {
          const p = readInternal();
          if (p) await pastePayload(p);
        }
      });
    }
  };
  document.addEventListener('copy', onCopy);
  document.addEventListener('cut', onCut);
  document.addEventListener('paste', onPaste);
  return () => {
    document.removeEventListener('copy', onCopy);
    document.removeEventListener('cut', onCut);
    document.removeEventListener('paste', onPaste);
  };
}

/** "Import SVG…": pick .svg files and put them onto the canvas. */
export function pickSvgFiles(): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.svg,image/svg+xml';
  input.multiple = true;
  input.onchange = async () => {
    for (const f of Array.from(input.files ?? [])) {
      if (!(await pasteSvg(await f.text()))) useApp().notify('error', t('import.svgInvalid', { name: f.name }));
    }
  };
  input.click();
}
